import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuiz } from '../contexts/QuizContext';
import { QuestionDisplay } from '../components/QuestionDisplay';
import { ExponentialProgressBar } from '../components/ExponentialProgressBar';
import { NetworkStatusIndicator } from '../components/NetworkStatusIndicator';
import { QuizRecoveryNotification } from '../components/QuizRecoveryNotification';
import { useQuizAutoSave } from '../hooks/useQuizAutoSave';
import {
  Card,
  CardBody,
  Section,
  Button,
  Badge
} from '../components/BaseComponents';

// Export this component so it can be mocked in tests
/**
 * QuizSubmissionLoader component displays an animated loading screen when a quiz is being submitted.
 * 
 * Features:
 * - Randomly selects one of five themed loading screens on mount
 * - Each screen has unique animations and visual elements
 * - Themes include: Brain Thinking, Knowledge Building, Rocket Launch, Growing Plant, and Achievement Badges
 * - Provides visual feedback to users while quiz results are being processed
 * 
 * The component uses various CSS animations and transitions to create engaging loading experiences
 * that match the educational context of the application.
 */
export const QuizSubmissionLoader: React.FC = () => {
  // Add state to track which loading screen to display
  const [loadingScreenType, setLoadingScreenType] = useState<number>(0);

  // Randomly select a loading screen when component mounts
  useEffect(() => {
    const randomScreen = Math.floor(Math.random() * 5);
    setLoadingScreenType(randomScreen);
  }, []);

  // Define the 5 different loading screen contents
  const loadingScreens = [
    // Screen 1: Original "Brain Thinking" design
    <div className="text-center max-w-md px-4">
      <div className="celebration mb-6">
        {/* Floating stars/icons animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            >
              {i % 3 === 0 ? '🎓' : i % 3 === 1 ? '⭐' : '✨'}
            </div>
          ))}
        </div>
      </div>

      <div className="transform animate-bounce-subtle">
        <span className="inline-block text-5xl mb-2">🎉</span>
        <h2 className="text-3xl font-bold mb-2 text-indigo-800">Great Job!</h2>
        <p className="text-xl mb-6 text-indigo-700">Quiz Successfully Completed</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 transform transition-all animate-scale-in">
        <p className="text-lg mb-4">Analyzing your responses...</p>

        {/* Brain thinking animation */}
        <div className="flex justify-center mb-4 relative">
          <div className="w-20 h-20 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-16 h-16" viewBox="0 0 24 24">
                <path
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  d="M10,13.5 C10,13.5 12,16 16,13.5 M8,9.5 C8,9.5 9,12 13,9.5"
                  className="animate-pulse"
                />
                <path
                  fill="#818cf8"
                  d="M12,21.5 C16.5,21.5 19,17 19,13.5 C19,11.5 17.5,10 16,10 C16,10 16,8.5 15,7.5 C14,6.5 12,6 10.5,7.5 C9,9 10,11.5 9,12.5 C8,13.5 5,13.5 5,16.5 C5,19.5 7.5,21.5 12,21.5 Z"
                  className="animate-pulse-slow"
                />
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-50 animate-ping-slow">
              <div className="w-12 h-12 rounded-full bg-indigo-200"></div>
            </div>
          </div>
        </div>

        {/* Progress meter */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 animate-progress-fill"></div>
        </div>
        <p className="text-sm text-gray-600">Preparing personalized insights...</p>
      </div>

      <p className="text-indigo-600 animate-pulse">Taking you to your results shortly!</p>
    </div>,

    // Screen 2: Knowledge Building Theme
    <div className="text-center max-w-md px-4">
      <div className="transform animate-bounce-subtle">
        <span className="inline-block text-5xl mb-2">📚</span>
        <h2 className="text-3xl font-bold mb-2 text-indigo-800">Knowledge Acquired!</h2>
        <p className="text-xl mb-6 text-indigo-700">Processing Your Learning Journey</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 transform transition-all animate-scale-in">
        <p className="text-lg mb-4">Building your knowledge profile...</p>

        {/* Stacking books animation */}
        <div className="flex justify-center mb-4 relative h-32">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`absolute bottom-0 w-20 h-8 rounded-sm animate-slide-up ${i % 2 === 0 ? 'gradient-purple-primary' : 'gradient-purple-secondary'}`}
              style={{
                transform: `translateY(${-i * 10}px)`,
                animationDelay: `${i * 0.5}s`,
                zIndex: 5 - i
              }}
            >
              <div className="h-full w-full flex items-center justify-center text-white text-xs font-bold">
                {['FACTS', 'CONCEPTS', 'SKILLS', 'WISDOM', 'MASTERY'][i]}
              </div>
            </div>
          ))}
        </div>

        {/* Progress meter */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 animate-progress-fill"></div>
        </div>
        <p className="text-sm text-gray-600">Calculating your knowledge growth...</p>
      </div>

      <p className="text-indigo-600 animate-pulse">Your results are almost ready!</p>
    </div>,

    // Screen 3: Rocket Launch / Space Theme
    <div className="text-center max-w-md px-4">
      <div className="transform">
        <span className="inline-block text-5xl mb-2">🚀</span>
        <h2 className="text-3xl font-bold mb-2 text-indigo-800">Mission Complete!</h2>
        <p className="text-xl mb-6 text-indigo-700">Launching Your Results</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 transform transition-all animate-scale-in">
        <p className="text-lg mb-4">Calculating your stellar performance...</p>

        {/* Rocket animation */}
        <div className="flex justify-center mb-4 relative h-40">
          <div className="absolute bottom-0 animate-rocket-launch">
            <div className="relative">
              <div className="text-4xl">🚀</div>
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-6 h-12 bg-gradient-to-t from-transparent to-orange-400 opacity-70 rounded-full animate-flame"></div>
            </div>
          </div>
          {/* Stars in background */}
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute text-yellow-300 animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                fontSize: `${Math.random() * 10 + 10}px`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random() * 2}s`
              }}
            >
              ✦
            </div>
          ))}
        </div>

        {/* Progress orbit */}
        <div className="w-full h-4 bg-gray-200 rounded-full mb-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-400 to-purple-600 animate-progress-fill"></div>
        </div>
        <p className="text-sm text-gray-600">Navigating to your results dashboard...</p>
      </div>

      <p className="text-indigo-600 animate-pulse">Prepare for touchdown in T-minus 3 seconds!</p>
    </div>,

    // Screen 4: Growing Plant / Nature Theme
    <div className="text-center max-w-md px-4">
      <div className="transform">
        <span className="inline-block text-5xl mb-2">🌱</span>
        <h2 className="text-3xl font-bold mb-2 text-green-800">Growth Complete!</h2>
        <p className="text-xl mb-6 text-green-700">Cultivating Your Results</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 transform transition-all animate-scale-in">
        <p className="text-lg mb-4">Nurturing your knowledge garden...</p>

        {/* Growing plant animation */}
        <div className="flex justify-center mb-4 relative h-40">
          <div className="absolute bottom-0 w-full max-w-[120px]">
            {/* Soil */}
            <div className="h-6 bg-gradient-to-r from-yellow-800 to-yellow-900 rounded-lg mx-auto"></div>

            {/* Plant stem */}
            <div className="w-2 h-0 bg-green-500 mx-auto animate-grow-height"></div>

            {/* Leaves appearing */}
            <div className="absolute bottom-8 left-1/2 w-8 h-0 bg-green-400 rounded-full -translate-x-full opacity-0 animate-leaf-left"></div>
            <div className="absolute bottom-16 left-1/2 w-8 h-0 bg-green-400 rounded-full translate-x-0 opacity-0 animate-leaf-right"></div>
            <div className="absolute bottom-24 left-1/2 w-8 h-0 bg-green-400 rounded-full -translate-x-full opacity-0 animate-leaf-left-2"></div>

            {/* Flower */}
            <div className="absolute bottom-32 left-1/2 -translate-x-1/2 opacity-0 animate-bloom">
              <div className="text-3xl">🌸</div>
            </div>
          </div>
        </div>

        {/* Progress soil meter */}
        <div className="w-full bg-gradient-to-r from-yellow-800 to-yellow-900 rounded-full h-3 mb-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-green-500 to-green-300 animate-progress-fill"></div>
        </div>
        <p className="text-sm text-gray-600">Harvesting insights from your quiz...</p>
      </div>

      <p className="text-green-600 animate-pulse">Your results are blossoming!</p>
    </div>,

    // Screen 5: Achievement Badges / Gamification Theme
    <div className="text-center max-w-md px-4">
      <div className="transform">
        <span className="inline-block text-5xl mb-2">🏆</span>
        <h2 className="text-3xl font-bold mb-2 text-yellow-700">Achievement Unlocked!</h2>
        <p className="text-xl mb-6 text-yellow-600">Calculating Your Rewards</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-lg mb-8 transform transition-all animate-scale-in">
        <p className="text-lg mb-4">Tallying your accomplishments...</p>

        {/* Badges spinning in */}
        <div className="flex justify-center mb-4 relative h-32">
          <div className="flex space-x-2">
            {['🥇', '🏅', '⭐', '💎', '🎖️'].map((badge, index) => (
              <div
                key={index}
                className="opacity-0 scale-0 animate-scale-in"
                style={{
                  animationDelay: `${index * 0.3}s`
                }}
              >
                <div
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-300 flex items-center justify-center text-2xl shadow-lg animate-spin-badge"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {badge}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 animate-progress-fill"></div>
        </div>
        <p className="text-sm text-gray-600">Preparing your achievement report...</p>
      </div>

      <p className="text-yellow-600 animate-pulse">Your trophy case awaits!</p>
    </div>,
  ];

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-indigo-100 z-50">
      {loadingScreens[loadingScreenType]}
    </div>
  );
}

/**
 * QuizPage component handles the quiz-taking experience.
 * It manages the flow between questions, tracks user answers,
 * and handles quiz navigation and completion.
 * 
 * Updated to use NewQuizContext with dynamic quiz generation.
 */
const QuizPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [tempAnswer, setTempAnswer] = useState<string>();
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);
  const [showRecoveryNotification, setShowRecoveryNotification] = useState(false);

  const {
    currentAttempt,
    loadQuizAttempt,
    questions,
    answers,
    currentQuestionIndex,
    moveToNextQuestion,
    moveToPreviousQuestion,
    submitAnswer,
    completeQuiz,
    getCurrentAnswer,
    quizProgress,
    isLoading,
    error
  } = useQuiz();

  // Initialize auto-save and recovery functionality
  const {
    loadQuizStateFromLocal,
    syncWithServer,
    pendingSyncCount,
    isOnlineState
  } = useQuizAutoSave();

  /**
   * Load quiz attempt when component mounts or attemptId changes
   */
  useEffect(() => {
    if (attemptId) {
      loadQuizAttempt(attemptId);
    }
  }, [attemptId, loadQuizAttempt]);

  /**
   * Auto-sync when connection is restored and we have pending changes
   */
  useEffect(() => {
    if (isOnlineState && pendingSyncCount > 0 && currentAttempt?.status === 'in_progress') {
      syncWithServer();
    }
  }, [isOnlineState, pendingSyncCount, currentAttempt?.status, syncWithServer]);

  /**
   * Resets the temporary answer when moving to a different question
   * and loads the current answer for the new question
   */
  useEffect(() => {
    // Reset temp answer when question changes and load current answer
    const currentAnswer = getCurrentAnswer();
    setTempAnswer(currentAnswer);
  }, [currentQuestionIndex, getCurrentAnswer]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <p className="text-xl">Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <p className="text-xl text-red-600">Error: {error}</p>
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="mt-4"
        >
          Go Back
        </Button>
      </div>
    );
  }

  if (!currentAttempt || !questions.length) {
    return (
      <div className="container mx-auto py-12 px-4 text-center">
        <p className="text-xl">Quiz not found or no questions available.</p>
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="mt-4"
        >
          Go Back
        </Button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = getCurrentAnswer();
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  /**
   * Updates the temporary answer state when the user selects an answer
   * @param answer - The user's selected answer
   */
  const handleAnswerChange = (answer: string | number | string[]) => {
    setTempAnswer(String(answer));
  };

  /**
   * Handles moving to the next question:
   * 1. Submits the current answer if it exists
   * 2. Advances to the next question if not on the last question
   */
  const handleNextQuestion = async () => {
    if (tempAnswer !== undefined && tempAnswer !== currentAnswer) {
      try {
        await submitAnswer(tempAnswer);
      } catch (error) {
        console.error('Failed to submit answer:', error);
        return; // Don't proceed if submission fails
      }
    }

    if (!isLastQuestion) {
      moveToNextQuestion();
    }
  };

  /**
   * Handles quiz completion:
   * 1. Submits the final answer if it exists
   * 2. Shows completion animation
   * 3. Marks the quiz as finished
   * 4. Navigates to the results page after animation completes
   */
  const handleFinishQuiz = async () => {
    try {
      // Submit final answer if it's different from current
      if (tempAnswer !== undefined && tempAnswer !== currentAnswer) {
        await submitAnswer(tempAnswer);
      }

      // Show completion animation
      setShowCompletionAnimation(true);

      // Complete the quiz and get results
      await completeQuiz();

      setTimeout(() => {
        navigate(`/results/${attemptId}`);
      }, 3000);
    } catch (error) {
      console.error('Failed to complete quiz:', error);
      // Still navigate to results even if there's an error
      setTimeout(() => {
        navigate(`/results/${attemptId}`);
      }, 1000);
    }
  };

  /**
   * Handles navigation to the previous question or back to the previous page
   * if already on the first question
   */
  const handlePreviousClick = () => {
    if (currentQuestionIndex === 0) {
      // If on the first question, navigate back to the previous page
      navigate(-1);
    } else {
      // Otherwise, move to the previous question
      moveToPreviousQuestion();
    }
  };

  /**
   * Handles recovering quiz from local backup
   */
  const handleRecoverQuiz = () => {
    if (attemptId) {
      const backup = loadQuizStateFromLocal(attemptId);
      if (backup) {
        // Load the quiz attempt normally, but the auto-save hook will handle restoration
        loadQuizAttempt(attemptId);
        setShowRecoveryNotification(false);
      }
    }
  };

  /**
   * Handles dismissing the recovery notification and starting fresh
   */
  const handleDismissRecovery = () => {
    setShowRecoveryNotification(false);
    if (attemptId) {
      loadQuizAttempt(attemptId);
    }
  };

  // If showing completion animation, render the animation screen
  if (showCompletionAnimation) {
    return <QuizSubmissionLoader />
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Network Status Indicator */}
      <div className="mb-4 flex justify-end">
        <NetworkStatusIndicator />
      </div>

      {/* Recovery Notification */}
      {showRecoveryNotification && attemptId && (
        <Section className="mb-8">
          <QuizRecoveryNotification
            attemptId={attemptId}
            onRecover={handleRecoverQuiz}
            onDismiss={handleDismissRecovery}
          />
        </Section>
      )}

      <Section title={currentAttempt.title || `Quiz #${currentAttempt.attemptNumber}`} subtitle="Answer all questions to complete the quiz" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
        <div className="flex items-center gap-2">
          <Badge className={
            currentAttempt.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
              currentAttempt.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                currentAttempt.difficulty === 'hard' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
          }>
            {currentAttempt.difficulty.charAt(0).toUpperCase() + currentAttempt.difficulty.slice(1)}
          </Badge>
          {!isOnlineState && (
            <Badge variant="warning">
              Working offline - progress saved locally
            </Badge>
          )}
          {pendingSyncCount > 0 && (
            <Badge variant="info">
              {pendingSyncCount} change{pendingSyncCount !== 1 ? 's' : ''} pending sync
            </Badge>
          )}
        </div>
      </Section>

      <Section className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
        <Card className="border border-neutral-200" noHover={true}>
          <CardBody>
            <div className="flex justify-between items-center mb-4">
              <span className="font-medium text-neutral-900">Quiz Progress</span>
            </div>
            <ExponentialProgressBar progress={quizProgress.progressPercentage / 100} />
          </CardBody>
        </Card>
      </Section>

      {currentQuestion && (() => {
        // Find the corresponding answer record to get the correct questionOrder
        const currentAnswerRecord = answers.find(answer => answer.questionId === currentQuestion.questionId);
        const questionOrder = currentAnswerRecord?.questionOrder || currentQuestionIndex + 1;

        return (
          <Section className="mb-8">
            <Card className="border border-neutral-200" noHover={true}>
              <CardBody>
                <QuestionDisplay
                  questionResult={{
                    question: {
                      ...currentQuestion,
                      id: currentQuestion.questionId.toString(),
                      correctAnswer: currentQuestion.answerKey
                    } as any,
                    userAnswer: tempAnswer !== undefined ? tempAnswer : currentAnswer,
                    isCorrect: false, // We don't know correctness until submission
                    questionOrder
                  }}
                  userAnswer={tempAnswer !== undefined ? tempAnswer : currentAnswer}
                  onAnswerSubmit={handleAnswerChange}
                  onMoveToNext={handleNextQuestion}
                  shuffleSeed={attemptId}
                />
              </CardBody>
            </Card>
          </Section>
        );
      })()}

      <Section className="mb-8">
        <div className="flex justify-between">
          <Button
            onClick={handlePreviousClick}
            variant="secondary"
          >
            {currentQuestionIndex === 0 ? 'Cancel' : 'Previous'}
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleFinishQuiz}
              disabled={isLoading || (tempAnswer === undefined && !currentAnswer)}
              variant="primary"
              className="bg-success-500 hover:bg-success-600"
            >
              {isLoading ? 'Submitting...' : 'Finish Quiz'}
            </Button>
          ) : (
            <Button
              onClick={handleNextQuestion}
              disabled={isLoading || (tempAnswer === undefined && !currentAnswer)}
              variant="primary"
            >
              {isLoading ? 'Submitting...' : 'Next'}
            </Button>
          )}
        </div>
      </Section>
    </div>
  );
};

export default QuizPage;